<template>
    <span :class="containerClass">
        <span
            :class="{
                'd-block': !inline,
                [priceClass]: true,
            }"
            >{{ formatPrice(defaultCurrencyPrice, defaultCurrency) }}</span
        >

        <span
            v-if="defaultCurrency.id !== activeCurrency.id && showNewCurrency"
            :class="{
                'd-block': currencyInNewline,
                [convertedPriceClass]: true,
            }"
            style="font-size: 12px !important"
        >
            ({{ formatPrice(activeCurrencyPrice, activeCurrency) }})
        </span>
    </span>
</template>

<script>
import {sprintf} from 'sprintf-js';

export default {
    props: {
        price: {
            required: true,
        },
        withPrefix: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
        prefix: {
            type: String,
            required: false,
            default() {
                return '';
            },
        },
        containerClass: {
            type: String,
            required: false,
            default() {
                return 'price';
            },
        },
        priceClass: {
            type: String,
            required: false,
            default() {
                return '';
            },
        },
        convertedPriceClass: {
            type: String,
            required: false,
            default() {
                return '';
            },
        },
        inline: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
        currency: {
            type: Object,
            required: false,
            default() {
                return null;
            },
        },
        fixedExchangeRate: {
            type: Number,
            required: false,
            default() {
                return null;
            },
        },
        precision: {
            type: Number,
            required: false,
            default() {
                return null;
            },
        },
        currencyInNewline: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
        showNewCurrency: {
            type: Boolean,
            required: false,
            default() {
                return true;
            },
        },
    },

    computed: {
        /**
         * @returns {object}
         */
        defaultCurrency() {
            return DefaultCurrency;
        },

        /**
         * @returns {object}
         */
        activeCurrency() {
            // In the case the component got an active currency, we have to force the usage
            // of this one.
            if (typeof this.currency === 'object' && this.currency !== null) {
                return this.currency;
            }

            return Currency;
        },

        /**
         * Assure the correct data type of price property.
         *
         * @returns {number}
         */
        castedPrice() {
            // Parse the price into a string.
            const price = this.price.toString();
            const test = price.match(/([0-9]+,[0-9]+)/) || [];

            // In the case we have a match, which indicates an unexpected format.
            if (test.length > 0) {
                return parseFloat(test[1].replace(',', '.'));
            }

            // Otherwise, we have to check for some placeholders like xx,xx
            if ((price.match(/x{1,2},x{1,2}/i) || []).length) {
                return 0;
            }

            return parseFloat(price);
        },

        /**
         * Apply the exchange rate of default currency to the price.
         *
         * @returns {number}
         */
        defaultCurrencyPrice() {
            return this.applyExchangeRate(this.castedPrice, this.defaultCurrency, null);
        },

        /**
         * Apply the exchange rate of the active currency.
         *
         * @returns {number}
         */
        activeCurrencyPrice() {
            return this.applyExchangeRate(this.castedPrice, this.activeCurrency, this.fixedExchangeRate);
        },

        /**
         * Determine the prefix to use for price display.
         *
         * @returns {string}
         */
        formattedPrefix() {
            return this.prefix + (this.withPrefix ? (this.castedPrice < 0 ? '- ' : '+ ') : '');
        },
    },

    methods: {
        /**
         * Apply the exchange rate of currency to price.
         *
         * @param {number} price
         * @param {object} currency
         * @param {number|null} exchangeRate
         *
         * @returns {number}
         */
        applyExchangeRate(price, currency, exchangeRate) {
            // In the case we don't have an exchange rate, we have to check the currency
            // to make sure the have an exchange rate to apply.
            if (typeof exchangeRate !== 'number' || isNaN(exchangeRate)) {
                exchangeRate = 1;

                // In the case the currency contains a usable exchange rate, we have
                // to apply them to assure the price will be displayed correctly.
                if (typeof currency.exchange_rate === 'number' && !isNaN(currency.exchange_rate)) {
                    exchangeRate = currency.exchange_rate / currency.exchange_rate_factor;
                }
            }

            return Math.abs(price) * exchangeRate;
        },

        /**
         * Apply the configured format of price.
         *
         * @param {number} price
         * @param {object} currency
         *
         * @returns {string}
         */
        formatPrice(price, currency) {
            // Default precision of rounding.
            let precision = this.precision;

            // In the case we don't have a customized configuration of precision.
            if (precision === null && typeof currency.precision === 'number' && !isNaN(currency.precision)) {
                precision = currency.precision;
            } else {
                precision = 2;
            }

            // Before we format the price, we have to apply the rounding.
            price = this.round(price, currency.rounding, precision);

            return this.formattedPrefix + sprintf(currency.format, price, currency.symbol);
        },

        /**
         * @param {number} value
         * @param {string} method
         * @param {number} precision
         *
         * @returns {number}
         */
        round(value, method, precision) {
            switch (method) {
                case 'up':
                case 'ceil':
                    return Math.ceil(value);

                case 'down':
                case 'floor':
                    return Math.floor(value);

                case 'default':
                default:
                    // eslint-disable-next-line no-case-declarations
                    const multiplier = Math.pow(10, precision || 0);

                    return Math.round(value * multiplier) / multiplier;
            }
        },
    },
};
</script>
